import { useEffect, useRef } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Calendar } from "lucide-react";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import FormTable, { ComplaintsTableColumns } from "@/components/shared/FormTable";

// ─── Options ─────────────────────────────────────────────────────────────────

const COMPLAINT_OPTIONS = [
  "Fever", "Cough (Dry)", "Cough (Productive)", "Cold / Runny Nose",
  "Sore Throat", "Headache", "Body Ache", "Fatigue / Weakness",
  "Abdominal Pain", "Nausea", "Vomiting", "Diarrhea", "Constipation",
  "Burning Sensation (Acidity)", "Bloating / Gas", "Loss of Appetite",
  "Back Pain", "Knee Pain", "Joint Pain", "Neck Pain", "Shoulder Pain",
  "Burning Urination", "Frequent Urination", "Chest Pain",
  "Shortness of Breath", "Palpitations", "Swelling (Legs / Face)",
  "Dizziness", "Numbness / Tingling", "Tremors", "Fainting / Syncope",
  "Convulsions", "Eye Pain", "Blurred Vision", "Ear Pain", "Ear Discharge",
  "Hoarseness", "Difficulty Swallowing", "Skin Rash", "Itching",
  "Hair Loss", "Mouth Ulcers", "Jaundice", "Blood in Stool",
  "Blood in Urine", "Excessive Thirst", "Excessive Urination",
  "Weight Loss", "Weight Gain", "Hip Pain", "Ankle Pain", "Heel Pain",
  "Wrist Pain", "Stiffness", "Swollen Joint", "Difficulty Walking",
  "Limping", "Muscle Pain", "Missed Period", "Irregular Periods",
  "Heavy Periods", "Painful Periods", "Vaginal Discharge", "Pelvic Pain",
  "Breast Pain", "Breast Lump", "Hot Flashes", "Crying Excessively",
  "Poor Feeding", "Not Gaining Weight", "Delayed Milestones",
  "Bed Wetting", "Throat Pain", "Sleep Problems", "Anxiety", "Depression",
  "Toothache", "Hiccups",
];

const FREQUENCY_OPTIONS = [
  "Continuous", "Multiple times daily", "3-4 times daily",
  "Twice daily", "Once daily", "Occasionally",
  "At night only", "In morning only", "After meals", "Before meals",
  "On exertion",
];

const SEVERITY_OPTIONS = ["Mild", "Moderate", "Severe", "Very severe"];

const DURATION_OPTIONS = [
  "Since today", "Since yesterday", "Since last night",
  "1 day", "2 days", "3 days",
  "1 week", "2 weeks",
  "1 month", "2 months", "3 months",
  "Chronic",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const calculateDateFromDuration = (duration: string): string => {
  const today = new Date();
  const dl = duration.toLowerCase().trim();
  if (dl.includes("today") || dl.includes("morning"))
    return today.toISOString().split("T")[0];
  if (dl.includes("yesterday") || dl.includes("last night")) {
    const d = new Date(today); d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  }
  const dayMatch = /(\d+)\s*day/.exec(dl);
  if (dayMatch) { const d = new Date(today); d.setDate(d.getDate() - parseInt(dayMatch[1])); return d.toISOString().split("T")[0]; }
  const weekMatch = /(\d+)\s*week/.exec(dl);
  if (weekMatch) { const d = new Date(today); d.setDate(d.getDate() - parseInt(weekMatch[1]) * 7); return d.toISOString().split("T")[0]; }
  const monthMatch = /(\d+)\s*month/.exec(dl);
  if (monthMatch) { const d = new Date(today); d.setMonth(d.getMonth() - parseInt(monthMatch[1])); return d.toISOString().split("T")[0]; }
  return "";
};

const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return dateStr; }
};

// showPicker() isn't supported on older Safari/iPadOS — fall back to focus,
// which still opens the native picker on those browsers.
const openDatePicker = (input: HTMLInputElement | null) => {
  if (!input) return;
  if (typeof input.showPicker === "function") input.showPicker();
  else input.focus();
};

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_ROW = { complaint: "", frequency: "", severity: "", duration: "", date: "" };

// ─── Component ────────────────────────────────────────────────────────────────

export default function ComplaintsGrid() {
  const { control, setValue, watch } = useFormContext();
  const { fields, append, remove }   = useFieldArray({ control, name: "complaints" });
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const dateRefs  = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (fields.length === 0) append(DEFAULT_ROW);
  }, [fields.length, append]);

  const watched = watch("complaints") || [];

  // Auto-add: new row when last complaint is filled, up to 10
  useEffect(() => {
    if (fields.length === 0) return;
    const last = watched[watched.length - 1];
    if (last?.complaint?.trim() && fields.length < 10) append(DEFAULT_ROW);
  }, [watched.map((c: any) => c?.complaint).join(",")]);

  const handleDurationChange = (index: number, duration: string) => {
    setValue(`complaints.${index}.duration`, duration, { shouldDirty: true });
    const date = duration.trim() ? calculateDateFromDuration(duration) : "";
    setValue(`complaints.${index}.date`, date, { shouldDirty: true });
  };

  const focusNext = (index: number, col: number) => {
    const next = inputRefs.current[index * 4 + col];
    if (next) setTimeout(() => next.focus(), 50);
  };

  // ── Cell renderer ────────────────────────────────────────────────────────────

  const renderCell = (field: any, columnKey: string, index: number) => {
    const complaint = watched[index]?.complaint || "";
    const dateVal   = watched[index]?.date || "";
    const isEmpty   = !complaint.trim();

    switch (columnKey) {

      case "complaint":
        return (
          <AutocompleteInput
            value={complaint}
            onChange={(val) => setValue(`complaints.${index}.complaint`, val, { shouldDirty: true })}
            options={COMPLAINT_OPTIONS}
            placeholder="Add complaint..."
            onEnterNext={() => focusNext(index, 1)}
            showAllOnFocus={true}
            dropdownMinWidth={220}
            ref={(el) => { inputRefs.current[index * 4] = el; }}
          />
        );

      case "frequency":
        return (
          <AutocompleteInput
            value={watched[index]?.frequency || ""}
            onChange={(val) => setValue(`complaints.${index}.frequency`, val, { shouldDirty: true })}
            options={FREQUENCY_OPTIONS}
            placeholder=""
            onEnterNext={() => focusNext(index, 2)}
            showAllOnFocus={true}
            dropdownMinWidth={160}
            ref={(el) => { inputRefs.current[index * 4 + 1] = el; }}
          />
        );

      case "severity":
        return (
          <AutocompleteInput
            value={watched[index]?.severity || ""}
            onChange={(val) => setValue(`complaints.${index}.severity`, val, { shouldDirty: true })}
            options={SEVERITY_OPTIONS}
            placeholder=""
            onEnterNext={() => focusNext(index, 3)}
            showAllOnFocus={true}
            dropdownMinWidth={120}
            ref={(el) => { inputRefs.current[index * 4 + 2] = el; }}
          />
        );

      case "duration":
        return (
          <AutocompleteInput
            value={watched[index]?.duration || ""}
            onChange={(val) => handleDurationChange(index, val)}
            options={DURATION_OPTIONS}
            placeholder=""
            onEnterNext={() => focusNext(index, 3)}
            showAllOnFocus={true}
            dropdownMinWidth={140}
            ref={(el) => { inputRefs.current[index * 4 + 3] = el; }}
          />
        );

      case "date":
        return (
          <div className="relative px-2 py-1">
            {!isEmpty && (
              <>
                <div
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => openDatePicker(dateRefs.current[index])}
                >
                  <Calendar className="w-3 h-3 text-muted-foreground/40 flex-shrink-0" />
                  {dateVal ? (
                    <span className="text-xs text-foreground">{formatDateDisplay(dateVal)}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground/40">Select date</span>
                  )}
                </div>
                <input
                  ref={(el) => { dateRefs.current[index] = el; }}
                  type="date"
                  value={dateVal}
                  onChange={(e) => setValue(`complaints.${index}.date`, e.target.value, { shouldDirty: true })}
                  className="absolute bottom-0 left-0 opacity-0 w-px h-px pointer-events-none"
                  tabIndex={-1}
                />
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <FormTable
      title="Complaint"
      columns={ComplaintsTableColumns}
      fields={fields}
      watched={watched}
      renderCell={renderCell}
      onAddRow={() => append(DEFAULT_ROW)}
      onRemoveRow={(index) => remove(index)}
      canRemoveRow={() => fields.length > 1}
      addButtonText="Add Complaint"
      showRowNumbers={true}
      autoAddRow={false}
      rowSize="compact"
    />
  );
}
